import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../lib/tracking';

const RyadHero = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleWhatsAppReservation = () => {
    // Facebook Pixel - InitiateCheckout (intention de reservation forte)
    trackEvent('InitiateCheckout', {
      content_name: 'Reservation VEFA - Hero CTA',
      content_category: 'Reservation',
    });
    const message = encodeURIComponent(`Bonjour, je souhaite réserver un appartement à la Résidence RYAD en VEFA.`);
    window.open(`https://wa.me/221787172828?text=${message}`, '_blank');
  };

  const handleCatalogDownload = () => {
    // Facebook Pixel - ViewContent (consultation catalogue PDF)
    trackEvent('ViewContent', {
      content_name: 'Catalogue PDF RYAD',
      content_type: 'document',
    });
    window.open('https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/67gzfaj3_CATALOGUE%20RYAD%20residence.pdf', '_blank');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video/Image with enhanced overlay */}
      <div className="absolute inset-0 z-0">
        <video 
          className="w-full h-full object-cover scale-105"
          autoPlay 
          muted 
          loop
          playsInline
        >
          <source src="https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/f7w20qow_Pr%C3%A9sentation%20de%20la%20r%C3%A9sidence%20RYAD%20%28F4%29.MOV" type="video/mp4" />
          <img 
            src="https://customer-assets.emergentagent.com/job_luxury-ryad/artifacts/8m0mf6b4_SALON%201.jpg" 
            alt="Résidence RYAD - Modern living room"
            className="w-full h-full object-cover"
          />
        </video>
        {/* Enhanced gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 to-transparent"></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl"></div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
        {/* Location Badge - Animated */}
        <div className={`transform transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-flex items-center glass px-8 py-3 rounded-full mb-8 border border-amber-400/30 hover:border-amber-400/60 transition-all duration-300 cursor-default group">
            <div className="w-3 h-3 bg-amber-400 rounded-full mr-3 animate-pulse"></div>
            <span className="font-medium text-lg tracking-wide text-amber-100 group-hover:text-amber-300 transition-colors">{t.hero.location}</span>
          </div>
        </div>

        {/* Main Title - Luxury Typography */}
        <h1 className={`font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight transform transition-all duration-1000 delay-200 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <span className="block text-white/90 text-2xl md:text-3xl font-light tracking-[0.3em] uppercase mb-4">
            Bienvenue à la
          </span>
          <span className="gradient-text drop-shadow-2xl">
            {t.hero.title}
          </span>
        </h1>

        {/* Subtitle - Elegant */}
        <p className={`text-xl md:text-2xl lg:text-3xl mb-6 font-light tracking-wide text-white/90 transform transition-all duration-1000 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {t.hero.subtitle}
        </p>

        {/* Key Info - Modern badges */}
        <div className={`mb-12 transform transition-all duration-1000 delay-400 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {['F2', 'F3', 'F4', 'Penthouse'].map((type, index) => (
              <span 
                key={type}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20 hover:bg-amber-500/30 hover:border-amber-400/50 transition-all duration-300 cursor-default"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {type}
              </span>
            ))}
          </div>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {t.hero.description}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-amber-400 font-semibold text-lg">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            {t.hero.timeline}
          </div>
        </div>

        {/* Call to Actions - Premium buttons */}
        <div className={`flex flex-col sm:flex-row gap-6 justify-center items-center transform transition-all duration-1000 delay-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <button
            onClick={handleWhatsAppReservation}
            className="group relative bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white px-10 py-5 rounded-2xl font-semibold text-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-xl hover:shadow-green-500/30 flex items-center btn-shine overflow-hidden"
          >
            <svg className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
            <span className="relative z-10">{t.hero.cta.reserve}</span>
          </button>

          <button
            onClick={handleCatalogDownload}
            className="group glass hover:bg-white/20 text-white px-10 py-5 rounded-2xl font-semibold text-lg transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 border border-white/30 hover:border-amber-400/50 flex items-center"
          >
            <svg className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t.hero.cta.catalog}
          </button>
        </div>

        {/* Stats bar */}
        <div className={`mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto transform transition-all duration-1000 delay-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-amber-400">R+5</div>
            <div className="text-sm text-gray-400 mt-1">Étages</div>
          </div>
          <div className="text-center border-x border-white/20">
            <div className="text-3xl md:text-4xl font-bold text-amber-400">4</div>
            <div className="text-sm text-gray-400 mt-1">Types d'apparts</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-amber-400">18</div>
            <div className="text-sm text-gray-400 mt-1">Mois de travaux</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Enhanced */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <button 
          onClick={() => scrollToSection('projet')}
          className="group flex flex-col items-center text-white/60 hover:text-amber-400 transition-all duration-300"
        >
          <span className="text-xs uppercase tracking-widest mb-2 opacity-0 group-hover:opacity-100 transition-opacity">Découvrir</span>
          <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-current rounded-full animate-bounce"></div>
          </div>
        </button>
      </div>
    </section>
  );
};

export default RyadHero;
